import { BanIcon, CheckIcon } from '@heroicons/react/solid';
import React, { useEffect, useMemo, useState } from 'react';
import { MdDelete, MdEdit } from 'react-icons/md';
import { Column, Row } from 'react-table';
import { ResGetStation } from '@gettactic/api';
import { useStations } from '@/hooks/useStations';
import { TableHeaderContentRenderer } from '@/components/basic';
import { PaginatedTable } from '@/components/basic/Table/PaginatedTable';
import { SelectOffices } from '@/components/partial/SelectOffices';
import { DeleteStationModal } from '@/components/station/DeleteStationModal';
import { EditStationModal } from '@/components/station/EditStationModal';
import { AdminVisitorsIndexProps } from '../admin/visitors/AdminVisitorsIndex';

export default function StationsTable({ isRedesign = false, officeId }: AdminVisitorsIndexProps) {
  const [office, setOffice] = useState<string[]>(officeId ? [officeId] : []);
  const stations = useStations({ office, limit: 100 });
  useEffect(() => {
    setOffice(officeId ? [officeId] : []);
  }, [officeId]);
  const [editStationModalOpen, setEditStationModalOpen] =
    useState<null | ResGetStation>(null);
  const [deleteStationModalOpen, setDeleteStationModalOpen] =
    useState<null | ResGetStation>(null);
  const data = useMemo(
    () =>
      (stations.data?.pages || [])
        .map((x) => x?.elements ?? [])
        .reduce((arr, els) => {
          return arr.concat(els);
        }, []),
    [stations?.data]
  );

  const columns: Column<ResGetStation>[] = React.useMemo(() => {
    const cols: Column<ResGetStation>[] = [
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div className="pl-4">Name</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'name',
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return <span className="pl-4">{row.original.name}</span>;
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div className="pl-4">ID</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'id',
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return <span className="pl-4">{row.original.id}</span>;
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Office</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'office',
        accessor: 'office.name'
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Status</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'status',
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return <span className="capitalize">{row.original.status}</span>;
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Enabled</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'is_enabled',
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return (
            <span>
              {row.original.is_enabled ? (
                <CheckIcon className="ml-2 h-5 w-5 text-green-500" />
              ) : (
                <BanIcon className="ml-2 h-5 w-5 text-gray-500" />
              )}{' '}
            </span>
          );
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Badges</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'settings.badges_enabled',
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return (
            <span>
              {row.original.settings.badges_enabled ? (
                <CheckIcon className="ml-2 h-5 w-5 text-green-500" />
              ) : (
                <BanIcon className="ml-2 h-5 w-5 text-gray-500" />
              )}{' '}
            </span>
          );
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Phone</div>
            </TableHeaderContentRenderer>
          );
        },
        id: 'settings.phone_number_enabled',
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return (
            <span>
              {row.original.settings.phone_number_enabled ? (
                <CheckIcon className="ml-2 h-5 w-5 text-green-500" />
              ) : (
                <BanIcon className="ml-2 h-5 w-5 text-gray-500" />
              )}{' '}
            </span>
          );
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div />
            </TableHeaderContentRenderer>
          );
        },
        id: 'actions',
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<ResGetStation> }) {
          return (
            <div className="flex flex-nowrap items-center p-4">
              <button
                onClick={() => setEditStationModalOpen(row.original)}
                className="px-2 text-gray-600 hover:text-gray-800"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={() => setDeleteStationModalOpen(row.original)}
                className="px-2 text-gray-600 hover:text-gray-800"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          );
        }
      }
    ];

    return cols;
  }, []);

  return (
    <div>
      {isRedesign ? null : <div className="my-4 flex flex-row items-center justify-between">
        <SelectOffices
          className="w-[12rem]"
          onChange={(office) => setOffice(office)}
          offices={office}
        />
      </div>}

      {editStationModalOpen ? (
        <EditStationModal
          setEditStation={setEditStationModalOpen}
          station={editStationModalOpen}
        />
      ) : null}
      {deleteStationModalOpen ? (
        <DeleteStationModal
          setDeleteModal={setDeleteStationModalOpen}
          station={deleteStationModalOpen}
        />
      ) : null}
      <div className="mt-4 min-h-[300px] text-xs sm:text-base">
        <PaginatedTable
          data={data}
          columns={columns}
          hasMoreData={stations.hasNextPage}
          loadMore={() => {
            stations.fetchNextPage();
          }}
          loading={stations.isFetchingNextPage}
        />
      </div>
    </div>
  );
}
